/**
 * CameraSystem — side-scroll camera rig that smoothly follows the midpoint
 * between two players (for co-op). Players move on X (horizontal) and Y (vertical/jump).
 * Camera is positioned on the Z axis looking toward Z=0.
 *
 * Phase-3 improvements (Task 9):
 *  - Frame-rate-independent lerp via CameraMath.frameRateIndependentLerpFactor
 *  - Horizontal lookahead from average physics-body velocity (READ-ONLY)
 *  - X and Y deadzones (no micro-jitter from standing still / small hops)
 *  - Biased vertical follow (lerps toward the lower/grounded cat so jumps
 *    don't yank the frame)
 *  - Additive screen-shake via triggerShake() — never baked into follow target
 *  - Tab-refocus: PLAYER_SWITCHED → decaying bias toward the newly-active cat
 *    (deferred to any active tutorial focus anchor from LiminalAtrium)
 *
 * Read-only invariant: update(dt) reads physics bodies via
 * body.getLinearVelocity() (read-only) and NEVER calls any setter on
 * bodies or meshes.
 */
import { Scene } from "@babylonjs/core/scene";
import { FreeCamera } from "@babylonjs/core/Cameras/freeCamera";
import { Matrix, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { PostProcess } from "@babylonjs/core/PostProcesses/postProcess";
import { Effect } from "@babylonjs/core/Materials/effect";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { AccessibilitySystem } from "./AccessibilitySystem";
import { CAMERA, PLAYER, VIEW } from "../config/GameConfig";
import {
  frameRateIndependentLerpFactor,
  lookaheadOffset,
  applyDeadzone,
  shakeDecay,
  focusWeightDecay,
} from "./CameraMath";
import { shouldCullAmbient } from "./AmbientCull";
import { gameEvents } from "../core/EventBus";
import { GameEvents } from "../config/events";
import { qaRulesActive } from "../qa/qaRules";

// Separation vignette — soft radial darkening that tightens as the cats
// drift apart. The book calls the shared frame "a quiet instructor," so
// we want a visible cue that telegraphs the PlayerEntity soft-clamp
// before a player notices they are being slowed. Strength ramps from 0
// to 1 over the back half of [MAX_SEPARATION - SOFT_BAND ... MAX_SEPARATION].
const VIGNETTE_FRAGMENT = `
varying vec2 vUV;
uniform sampler2D textureSampler;
uniform float strength;

void main(void) {
  vec4 color = texture2D(textureSampler, vUV);
  // Distance from center, scaled so corners ≈ 1.0.
  vec2 centered = vUV - vec2(0.5, 0.5);
  float d = length(centered) * 1.4142;
  // Smooth inner circle stays bright, outer darkens hard.
  float v = smoothstep(0.35, 0.9, d);
  // Darken proportional to strength; tint slightly cool so it reads
  // as "the frame closing in," not just "someone dimmed the screen."
  vec3 dimmed = mix(color.rgb, color.rgb * vec3(0.10, 0.11, 0.18), v * strength);
  gl_FragColor = vec4(dimmed, color.a);
}
`;
// One-time shader registration so every CameraSystem instance reuses it.
if (!Effect.ShadersStore["separationVignetteFragmentShader"]) {
  Effect.ShadersStore["separationVignetteFragmentShader"] = VIGNETTE_FRAGMENT;
}

/** Minimal read-only physics body interface — only the velocity read we need. */
export interface IPhysicsBodyReadOnly {
  getLinearVelocity(instanceIndex?: number): Vector3;
}

export interface CameraBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

export class CameraSystem {
  public camera: FreeCamera;
  private targets: AbstractMesh[] = [];
  private currentPosition: Vector3;
  private bounds: CameraBounds | null = null;
  private hasFocusAnchor = false;
  private focusWeight = 0;

  /** Pre-allocated vectors to avoid per-frame allocations. */
  private _targetPos = new Vector3();
  private _lookAt = new Vector3();
  private _focusAnchor = new Vector3();

  /** Separation-vignette post-process. Lazily attached on first update. */
  private vignette: PostProcess | null = null;
  private vignetteStrength = 0;

  // ── Additive screen-shake ──────────────────────────────────────────────
  /** Current shake magnitude (world units). Decayed every frame. */
  private _shakeMagnitude = 0;
  /** Random shake offset applied additively at the final position write. */
  private _shakeOffset = new Vector3();

  // ── Physics body references for lookahead (READ-ONLY) ────────────────
  /** Optional physics bodies for the two cats. Velocity is read-only. */
  private _physicsBodies: ReadonlyArray<IPhysicsBodyReadOnly | null> = [];

  // ── Tab-refocus state ─────────────────────────────────────────────────
  /** Index (0 = minna, 1 = basil) of the last-switched-to player. */
  private _switchFocusTargetIdx = -1;
  /** Remaining focus-bias weight for the Tab-refocus effect. */
  private _switchFocusWeight = 0;

  /** Event unsub for PLAYER_SWITCHED — cleaned up on dispose(). */
  private _unsubPlayerSwitched: (() => void) | null = null;

  // ── Ambient particle culling (QA-gated) ───────────────────────────
  /**
   * Ambient particle systems registered by the active level. When the
   * camera distance exceeds CAMERA.AMBIENT_CULL_DISTANCE their emitRate is
   * set to 0 and restored on approach.  The array stores pairs of
   * [particleSystem, originalEmitRate] so restore always returns the exact
   * prior value.  NOT mutated under QA (window.__qa present).
   */
  private _ambientPS: Array<{ ps: { emitRate: number }; originalRate: number }> = [];
  /** Whether culling is currently active (tracks hysteresis state). */
  private _ambientCulled = false;

  constructor(scene: Scene) {
    // Camera positioned on the Z axis, looking toward Z=0 (side-scroll perspective)
    const startPos = new Vector3(0, CAMERA.Y_OFFSET, CAMERA.Z_DISTANCE);
    this.camera = new FreeCamera("mainCamera", startPos, scene);
    this.camera.setTarget(new Vector3(0, CAMERA.Y_OFFSET, 0));
    this.camera.fov = CAMERA.FOV;
    if (VIEW.MIRROR_X) this.installViewMirror(scene);

    // Disable default camera controls — we drive the camera ourselves
    this.camera.inputs.clear();

    // Force initial view matrix computation so frustum culling works
    // immediately on newly-created scenes (e.g. after scene transitions).
    this.camera.getViewMatrix(true);

    this.currentPosition = startPos.clone();

    // Attach the separation vignette. It is a full-screen 1.0-ratio pass
    // with a single `strength` uniform. Strength is driven from update().
    try {
      this.vignette = new PostProcess(
        "separationVignette",
        "separationVignette",
        ["strength"],
        null,
        1.0,
        this.camera,
        Texture.BILINEAR_SAMPLINGMODE,
        scene.getEngine(),
        false,
      );
      this.vignette.onApply = (effect) => {
        effect.setFloat("strength", this.vignetteStrength);
      };
    } catch (e) {
      // Non-fatal — older WebGL contexts may reject the shader and we
      // simply render without the vignette.
      this.vignette = null;
      console.warn("[CameraSystem] separation vignette unavailable:", e);
    }

    // Subscribe to PLAYER_SWITCHED so Tab-switch triggers a temporary
    // bias toward the newly-active cat.
    // HUD already handles PLAYER_SWITCHED for the character indicator, and
    // AudioManager is fully event-driven — we don't need to fire additional
    // events; the existing `player:switched` event reaches both systems.
    //
    // Navigator-safe: this handler only writes to _switchFocusTargetIdx and
    // _switchFocusWeight (camera render state). It never writes to physics
    // bodies or meshes, so QA's setActivePlayer() emitting PLAYER_SWITCHED
    // during qa:fullplay is safe.
    this._unsubPlayerSwitched = gameEvents.on(
      GameEvents.PLAYER_SWITCHED,
      (player: 1 | 2) => {
        // Map player number (1-based) to our 0-based target array index.
        this._switchFocusTargetIdx = player - 1;
        this._switchFocusWeight = CAMERA.SWITCH_FOCUS_WEIGHT;
      },
    );
  }

  /** Set the meshes to follow (typically the two player capsules). */
  setTargets(meshes: AbstractMesh[]): void {
    this.targets = meshes;
  }

  /**
   * Optionally register physics bodies for the followed targets so the
   * camera can read horizontal velocity for the lookahead offset.
   * Pass `null` entries for targets without a body.
   * READ-ONLY: bodies are never mutated.
   */
  setPhysicsBodies(bodies: ReadonlyArray<IPhysicsBodyReadOnly | null>): void {
    this._physicsBodies = bodies;
  }

  /**
   * Compute the biased Y the steady-state update() targets:
   * 60% toward the lower (more-grounded) cat, 40% toward the midpoint.
   * Extracted so snapToTargets() and update() agree on frame 0.
   */
  private computeBiasedY(minY: number, midY: number): number {
    return minY * 0.6 + midY * 0.4;
  }

  /**
   * Snap the camera to its current-frame target midpoint with no lerp.
   * Use after `setTargets()` during level setup so the first rendered
   * frame already frames the cats instead of showing the default
   * `(0, Y_OFFSET, Z_DISTANCE)` start pose and lerping in.
   *
   * Without this, level-entry screenshots (and the first 10-20 frames a
   * player sees on level load) show an empty void while the camera
   * catches up. Identified in the 2026-04-18 visual review.
   */
  snapToTargets(): void {
    if (this.targets.length === 0) return;
    let sumX = 0;
    let sumY = 0;
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    for (const mesh of this.targets) {
      sumX += mesh.position.x;
      sumY += mesh.position.y;
      if (mesh.position.x < minX) minX = mesh.position.x;
      if (mesh.position.x > maxX) maxX = mesh.position.x;
      if (mesh.position.y < minY) minY = mesh.position.y;
    }
    const midX = sumX / this.targets.length;
    const midY = sumY / this.targets.length;
    const biasedY = this.computeBiasedY(minY, midY);
    const aspect = this.camera.getScene().getEngine().getAspectRatio(this.camera);
    const paddedSpan = Math.max(0, maxX - minX) + CAMERA.SPAN_PADDING;
    const requiredZ = paddedSpan / (2 * Math.tan(this.camera.fov / 2) * aspect);
    const zClamped = Math.max(
      CAMERA.Z_DISTANCE,
      Math.min(CAMERA.MAX_Z_DISTANCE, requiredZ),
    );
    this.currentPosition.set(midX, biasedY + CAMERA.Y_OFFSET, zClamped);
    if (this.bounds) {
      this.currentPosition.x = Math.min(
        this.bounds.maxX,
        Math.max(this.bounds.minX, this.currentPosition.x),
      );
      this.currentPosition.y = Math.min(
        this.bounds.maxY,
        Math.max(this.bounds.minY, this.currentPosition.y),
      );
    }
    this.camera.position.copyFrom(this.currentPosition);
    this._lookAt.set(this.currentPosition.x, this.currentPosition.y, 0);
    this.camera.setTarget(this._lookAt);
    this.camera.getViewMatrix(true);
  }

  setBounds(bounds: CameraBounds | null): void {
    this.bounds = bounds;
  }

  /**
   * Register ambient particle systems that should be culled when the camera
   * zooms far out (cinematic moments).  Accumulates entries — safe to call
   * multiple times (e.g. once per level layer or subsystem).
   *
   * The original emitRate is captured at registration time and restored
   * exactly when the camera returns to normal play distance or on dispose().
   *
   * GATED: mutations only apply when window.__qa is absent.  Under headless
   * QA the list is stored but never acted upon, so navigator timing is
   * unaffected.
   *
   * @param systems  Particle systems to manage.  Pass only the ambient
   *                 "background atmosphere" emitters — puzzle/ability
   *                 particle systems must NOT be registered here.
   */
  registerAmbientParticles(systems: Array<{ emitRate: number }>): void {
    this._ambientPS.push(...systems.map((ps) => ({ ps, originalRate: ps.emitRate })));
  }

  setFocusAnchor(anchor: Vector3 | null, weight: number): void {
    if (!anchor || weight <= 0) {
      this.hasFocusAnchor = false;
      this.focusWeight = 0;
      return;
    }
    this._focusAnchor.copyFrom(anchor);
    this.hasFocusAnchor = true;
    this.focusWeight = Math.max(0, Math.min(1, weight));
  }

  /**
   * Trigger an additive screen-shake.
   *
   * The shake is applied as a random XY offset on top of the follow
   * position — it is NEVER baked into the follow target so it doesn't
   * compete with the lerp. Decays linearly via shakeDecay each frame.
   *
   * Accessibility: reduced-motion players get zero shake (the call is
   * accepted but the magnitude is forced to 0).
   *
   * @param intensity   Peak magnitude in world units (e.g. 0.3)
   * @param durationMs  Duration hint in milliseconds — sets initial magnitude
   *                    so `intensity` is reached immediately and decays by
   *                    SHAKE_DECAY_PER_SEC. For fine control pass intensity
   *                    directly; durationMs is informational only here since
   *                    decay is driven by CAMERA.SHAKE_DECAY_PER_SEC.
   */
  triggerShake(intensity: number, durationMs: number): void {
    const a11y = AccessibilitySystem.getInstance().state;
    if (a11y.reducedMotion) return;
    // Take the max so overlapping shakes don't cancel each other.
    // The duration hint is currently unused for the decay (decay is
    // driven by SHAKE_DECAY_PER_SEC), but retained for API parity with
    // the old CutsceneSystem.executeShake duration parameter.
    void durationMs;
    this._shakeMagnitude = Math.max(this._shakeMagnitude, intensity);
  }

  /**
   * Update camera position — call every frame with frame delta time.
   *
   * @param dt  Frame delta in seconds (default 1/60 for legacy bare callers).
   */
  update(dt = 1 / 60): void {
    if (this.targets.length === 0) return;

    const a11y = AccessibilitySystem.getInstance().state;

    // ── 1. Compute midpoint + horizontal span + per-axis min/max ────────
    let sumX = 0;
    let sumY = 0;
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    for (const mesh of this.targets) {
      sumX += mesh.position.x;
      sumY += mesh.position.y;
      if (mesh.position.x < minX) minX = mesh.position.x;
      if (mesh.position.x > maxX) maxX = mesh.position.x;
      if (mesh.position.y < minY) minY = mesh.position.y;
    }
    const midX = sumX / this.targets.length;
    const midY = sumY / this.targets.length;

    // Biased vertical follow: lerp toward the lower (more-grounded) cat
    // instead of the strict midpoint. This prevents a jump arc from
    // pulling the frame upward — the camera only follows the floor.
    // Uses computeBiasedY so snapToTargets() agrees on frame 0.
    const biasedY = this.computeBiasedY(minY, midY);

    // ── 2. Horizontal lookahead (READ-ONLY velocity access) ──────────────
    let avgVx = 0;
    if (this._physicsBodies.length > 0) {
      let vxSum = 0;
      let count = 0;
      for (const body of this._physicsBodies) {
        if (body) {
          // READ-ONLY — no setter called on the body
          vxSum += body.getLinearVelocity().x;
          count++;
        }
      }
      if (count > 0) avgVx = vxSum / count;
    }
    const la = lookaheadOffset(avgVx, CAMERA.LOOKAHEAD_SCALE, CAMERA.LOOKAHEAD_MAX);

    // ── 3. Focus anchor blending (tutorial anchor deference) ────────────
    // Tutorial focus anchor (e.g. LiminalAtrium puzzle guide) takes
    // priority over the Tab-refocus bias. Only apply Tab-refocus when
    // no tutorial anchor is active.
    const hasTutorialAnchor = this.hasFocusAnchor && this.focusWeight > 0;

    let targetX = midX + la;
    let targetY = biasedY;

    if (hasTutorialAnchor) {
      // Defer to the level's focus anchor (tutorial step guide).
      const fw = this.focusWeight;
      targetX = (midX + la) * (1 - fw) + this._focusAnchor.x * fw;
      targetY = biasedY * (1 - fw) + this._focusAnchor.y * fw;
    } else if (
      this._switchFocusWeight > 0 &&
      this._switchFocusTargetIdx >= 0 &&
      this._switchFocusTargetIdx < this.targets.length
    ) {
      // Tab-refocus: temporarily bias toward the newly-active cat.
      // Lookahead (la) is hoisted outside the blend — it applies once to
      // the blended X, not duplicated in each term.
      const switchMesh = this.targets[this._switchFocusTargetIdx];
      const sw = this._switchFocusWeight;
      targetX = (midX * (1 - sw) + switchMesh.position.x * sw) + la;
      targetY = biasedY * (1 - sw) + switchMesh.position.y * sw;
      // Decay the switch focus weight this frame.
      this._switchFocusWeight = focusWeightDecay(
        this._switchFocusWeight,
        dt,
        CAMERA.SWITCH_FOCUS_DURATION,
      );
    }

    // ── 4. Span-aware Z target ───────────────────────────────────────────
    const aspect = this.camera.getScene().getEngine().getAspectRatio(this.camera);
    const paddedSpan = Math.max(0, maxX - minX) + CAMERA.SPAN_PADDING;
    const requiredZ = paddedSpan / (2 * Math.tan(this.camera.fov / 2) * aspect);
    const zTarget = Math.max(
      CAMERA.Z_DISTANCE,
      Math.min(CAMERA.MAX_Z_DISTANCE, requiredZ),
    );

    // ── 5. Apply deadzones ───────────────────────────────────────────────
    const dzTargetX = applyDeadzone(
      this.currentPosition.x,
      targetX + CAMERA.Y_OFFSET * 0, // X has no Y_OFFSET — kept explicit
      CAMERA.DEADZONE_X,
    );
    // Compute full Y target with offset first, then deadzone on that axis.
    const fullTargetY = targetY + CAMERA.Y_OFFSET;
    const dzTargetY = applyDeadzone(
      this.currentPosition.y,
      fullTargetY,
      CAMERA.DEADZONE_Y,
    );

    this._targetPos.set(dzTargetX, dzTargetY, zTarget);

    if (this.bounds) {
      this._targetPos.x = Math.min(this.bounds.maxX, Math.max(this.bounds.minX, this._targetPos.x));
      this._targetPos.y = Math.min(this.bounds.maxY, Math.max(this.bounds.minY, this._targetPos.y));
    }

    // ── 6. Frame-rate-independent lerp ──────────────────────────────────
    const alphaXY = frameRateIndependentLerpFactor(CAMERA.LERP_K, dt);
    const alphaY  = frameRateIndependentLerpFactor(CAMERA.VERTICAL_LERP_K, dt);
    const alphaZ  = frameRateIndependentLerpFactor(CAMERA.ZOOM_LERP_K, dt);

    this.currentPosition.x += (this._targetPos.x - this.currentPosition.x) * alphaXY;
    // Vertical uses its own (slower) alpha so jumps don't yank.
    this.currentPosition.y += (this._targetPos.y - this.currentPosition.y) * alphaY;
    this.currentPosition.z += (this._targetPos.z - this.currentPosition.z) * alphaZ;

    // ── 7. Additive screen-shake ─────────────────────────────────────────
    // Shake is applied on top of the follow position so it never
    // contaminates the lerp target. Skipped entirely for reducedMotion.
    if (this._shakeMagnitude > 0 && !a11y.reducedMotion) {
      this._shakeOffset.set(
        (Math.random() - 0.5) * 2 * this._shakeMagnitude,
        (Math.random() - 0.5) * 2 * this._shakeMagnitude,
        0,
      );
      this._shakeMagnitude = shakeDecay(
        this._shakeMagnitude,
        dt,
        CAMERA.SHAKE_DECAY_PER_SEC,
      );
    } else {
      this._shakeOffset.set(0, 0, 0);
      if (a11y.reducedMotion) this._shakeMagnitude = 0;
    }

    // ── 8. Write final camera position (follow + additive shake) ─────────
    this.camera.position.x = this.currentPosition.x + this._shakeOffset.x;
    this.camera.position.y = this.currentPosition.y + this._shakeOffset.y;
    this.camera.position.z = this.currentPosition.z + this._shakeOffset.z;

    this._lookAt.set(this.currentPosition.x, this.currentPosition.y, 0);
    this.camera.setTarget(this._lookAt);

    // ── 9. Ambient particle culling (QA-gated) ───────────────────────────
    // When zoomed far out (cinematic zoom / level-end reveal) the ambient
    // mote emitters are invisible noise at that scale. Cull their emitRate
    // to 0 to save GPU particle budget. Restore when the camera returns.
    // The entire block is skipped under headless QA so navigator timing is
    // unaffected.
    const qaActive =
      qaRulesActive();
    if (!qaActive && this._ambientPS.length > 0) {
      const nextCulled = shouldCullAmbient(
        this.currentPosition.z,
        CAMERA.AMBIENT_CULL_DISTANCE,
        CAMERA.AMBIENT_CULL_HYSTERESIS,
        this._ambientCulled,
      );
      if (nextCulled !== this._ambientCulled) {
        this._ambientCulled = nextCulled;
        for (const entry of this._ambientPS) {
          entry.ps.emitRate = nextCulled ? 0 : entry.originalRate;
        }
      }
    }

    // ── 10. Separation vignette ──────────────────────────────────────────
    // Ramp 0 → 1 over the back half of
    // [PLAYER.MAX_SEPARATION - SOFT_BAND .. MAX_SEPARATION + SOFT_BAND]
    // so players get a visible "frame closing in" cue slightly before
    // the soft-clamp in PlayerEntity bites.
    //
    // Accessibility: players with reducedMotion enabled get a weaker
    // and faster-settling vignette (half strength, easier ease) so the
    // "frame closing in" cue still fires but doesn't pulse or wobble.
    if (this.vignette && this.targets.length >= 2) {
      const dx = Math.abs(maxX - minX);
      const softStart = PLAYER.MAX_SEPARATION - PLAYER.SEPARATION_SOFT_BAND;
      const softEnd = PLAYER.MAX_SEPARATION + PLAYER.SEPARATION_SOFT_BAND;
      const t = (dx - softStart) / (softEnd - softStart);
      const ceiling = a11y.reducedMotion ? 0.5 : 1.0;
      const target = Math.max(0, Math.min(ceiling, t * ceiling));
      // Ease in smoothly — avoid a hard pop when players dance at the
      // edge. Reduced-motion mode uses a tighter ease so the indicator
      // settles instead of oscillating.
      const ease = a11y.reducedMotion ? 0.25 : 0.1;
      this.vignetteStrength =
        this.vignetteStrength * (1 - ease) + target * ease;
    }
  }

  /**
   * Mirror the rendered image horizontally so world +X is screen-right
   * (see VIEW.MIRROR_X). Negating clip-space X flips triangle winding, so
   * for the main camera's own pass the engine culls front faces instead;
   * render targets (shadow maps etc.) render with normal culling.
   */
  private installViewMirror(scene: Scene): void {
    const cam = this.camera;
    const engine = scene.getEngine();
    cam.onProjectionMatrixChangedObservable.add(() => {
      // Row-vector convention (v * M): negating column 0 negates clip x.
      // Read the freshly computed matrix directly (calling
      // getProjectionMatrix() from inside its own notification re-enters).
      const m = (cam as unknown as { _projectionMatrix: Matrix })._projectionMatrix;
      const a = Array.from(m.asArray());
      a[0] = -a[0]; a[4] = -a[4]; a[8] = -a[8]; a[12] = -a[12];
      Matrix.FromArrayToRef(a, 0, m);
    });
    const beforeTargets = scene.onBeforeRenderTargetsRenderObservable.add(() => { engine.cullBackFaces = null; });
    const afterTargets = scene.onAfterRenderTargetsRenderObservable.add(() => {
      if (scene.activeCamera === cam) engine.cullBackFaces = false;
    });
    const afterCamera = scene.onAfterCameraRenderObservable.add((c) => {
      if (c === cam) engine.cullBackFaces = null;
    });
    this._disposeMirror = () => {
      scene.onBeforeRenderTargetsRenderObservable.remove(beforeTargets);
      scene.onAfterRenderTargetsRenderObservable.remove(afterTargets);
      scene.onAfterCameraRenderObservable.remove(afterCamera);
      engine.cullBackFaces = null;
    };
  }

  private _disposeMirror: (() => void) | null = null;

  dispose(): void {
    this._disposeMirror?.();
    this._disposeMirror = null;
    if (this._unsubPlayerSwitched) {
      this._unsubPlayerSwitched();
      this._unsubPlayerSwitched = null;
    }
    if (this.vignette) {
      this.vignette.dispose();
      this.vignette = null;
    }
    // Restore every registered ambient PS to its original emitRate before
    // clearing, so none is left stuck at emitRate=0 if disposed while culled.
    for (const entry of this._ambientPS) {
      entry.ps.emitRate = entry.originalRate;
    }
    this._ambientPS = [];
  }
}
